<div class="drw-container">
  <div class="drw-header">
    <div class="drw-header-text">
      <span class="drw-title">Build offer</span>
      <span class="drw-subtitle"
        >Offer one or more payment plans on a single transaction.</span
      >
    </div>
    <button
      (click)="close()"
      mat-icon-button
      [disabled]="isSubmitting"
      aria-label="Close"
    >
      <mat-icon class="drw-close">close</mat-icon>
    </button>
  </div>

  <div class="drw-content">
    <section class="section">
      <div class="section-title">Customer</div>
      <div class="info-row">
        <span class="label">Customer ID</span>
        <span class="value tabular">{{ data.customer.id }}</span>
      </div>
      <div class="info-row">
        <span class="label">Business name</span>
        <span class="value">{{ data.customer.borrowerName }}</span>
      </div>
      <div class="info-row">
        <span class="label">Credit limit</span>
        <span class="value">{{ creditLimit | currency }}</span>
      </div>
      <div class="info-row">
        <span class="label">Available credit</span>
        <span class="value strong">{{ availableCredit | currency }}</span>
      </div>
    </section>

    <form [formGroup]="submitForm" class="submit-form">
      <section class="section">
        <div class="section-title">Amount</div>
        <label for="amount">Desired net amount</label>
        <mat-form-field appearance="fill" class="full-width">
          <span matPrefix>&nbsp;$</span>
          <input
            matInput
            formControlName="amount"
            id="amount"
            placeholder="0.00"
            type="text"
            mask="separator.2"
            [thousandSeparator]="','"
          />
          @if (submitForm.get("amount")?.hasError("required")) {
            <mat-error>Desired net amount is required</mat-error>
          }
          @if (submitForm.get("amount")?.hasError("min")) {
            <mat-error>Amount must be at least $0.01</mat-error>
          }
          @if (submitForm.get("amount")?.hasError("max")) {
            <mat-error
              >Amount can't exceed customer's remaining available
              credit</mat-error
            >
          }
          @if (submitForm.get("amount")?.hasError("pattern")) {
            <mat-error
              >Please enter a valid amount (up to 2 decimal places)</mat-error
            >
          }
        </mat-form-field>
        <p class="hint">
          The amount you want to receive after fees. Each offered plan is priced
          from this number.
        </p>

        <div class="gross-up-box">
          <label class="gross-up-toggle">
            <input
              type="checkbox"
              [checked]="grossUp"
              (change)="toggleGrossUp()"
            />
            <span>
              <span class="gross-up-title"
                >Gross up to cover processing fees</span
              >
              <span class="gross-up-desc"
                >Raise each plan's total so you net your target after your
                portion of that plan's MDR.</span
              >
            </span>
          </label>

          @if (grossUp) {
            <div class="split-panel">
              <div class="split-header">
                <span>Default fee split</span>
                <button
                  type="button"
                  class="tip-btn"
                  (mouseenter)="showMdrTip = true"
                  (mouseleave)="showMdrTip = false"
                  aria-label="MDR help"
                >
                  <mat-icon>info_outline</mat-icon>
                  @if (showMdrTip) {
                    <span class="tip-bubble">
                      <b>MDR</b> = Merchant Discount Rate — the cost of offering
                      the program to your buyer. Each plan has its own rate.
                    </span>
                  }
                </button>
              </div>
              <label for="splitPct">Buyer covers (% of each plan's MDR)</label>
              <div class="pct-input">
                <input
                  id="splitPct"
                  type="text"
                  inputmode="numeric"
                  [value]="splitPct"
                  (change)="onSplitChange($event)"
                  placeholder="0–100"
                />
                <span class="pct-suffix">%</span>
              </div>
              @if (splitEmpty) {
                <div class="warn-inline">
                  Enter the share of each plan's MDR the buyer covers. You can
                  override it per plan below.
                </div>
              }
              @if (sponsorStr) {
                <div class="sponsor-hint">{{ sponsorStr }}</div>
              }
            </div>
          }
        </div>
      </section>

      <section class="section">
        <div class="plans-header">
          <span class="section-title">Plans to offer</span>
          @if (selectedCount > 0) {
            <span class="count-pill ok">{{ countLabel }}</span>
          } @else {
            <span class="count-pill muted">Select at least one</span>
          }
        </div>

        @if (isLoadingCreditPlans) {
          <div class="loading-plans">
            <mat-spinner [diameter]="24"></mat-spinner>
          </div>
        } @else if (creditPlans.length === 0) {
          <mat-hint>
            No plans available. Please contact us at
            support&#64;transendfinancial.com or 833-378-3841
          </mat-hint>
        } @else {
          <div class="plan-list">
            @for (row of planRows; track row.plan.id) {
              <div
                class="plan-card"
                [class.on]="row.on"
                [class.over]="row.over"
                (click)="togglePlan(row.plan.id)"
              >
                <div class="plan-card-top">
                  <span class="check" [class.checked]="row.on">
                    @if (row.on) {
                      <mat-icon>check</mat-icon>
                    }
                  </span>
                  <div class="plan-meta">
                    <div class="plan-name-row">
                      <span class="plan-name">{{ row.plan.name }}</span>
                      <span class="mdr-badge">{{ row.mdrPct }}</span>
                    </div>
                    <div class="plan-desc">
                      {{ row.plan.description || row.plan.notes }}
                    </div>
                  </div>
                  <div class="plan-total">
                    <div class="total" [class.over-text]="row.over">
                      {{ row.totalStr }}
                    </div>
                    @if (row.feeStr) {
                      <div class="fee">{{ row.feeStr }}</div>
                    }
                  </div>
                </div>
                @if (row.over) {
                  <div class="plan-warn">
                    This plan's total exceeds the customer's available credit.
                  </div>
                }
                @if (row.showOverride) {
                  <div class="override-row" (click)="stopProp($event)">
                    <button
                      type="button"
                      class="link-btn"
                      (click)="toggleOverride(row.plan.id, $event)"
                    >
                      {{ row.overrideLabel }}
                    </button>
                    @if (row.custom) {
                      <span class="custom-pill">Custom</span>
                      <div class="pct-input override">
                        <input
                          type="text"
                          inputmode="numeric"
                          [value]="row.overrideVal"
                          (change)="onOverrideChange(row.plan.id, $event)"
                          placeholder="0–100"
                        />
                        <span class="pct-suffix">%</span>
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
        }
      </section>

      <section class="section fields">
        <label for="ponumber"
          >Purchase order number <span class="optional">(optional)</span></label
        >
        <mat-form-field appearance="fill" class="full-width">
          <input
            matInput
            formControlName="ponumber"
            id="ponumber"
            placeholder="Order number"
          />
        </mat-form-field>

        <label for="inumber"
          >Invoice number <span class="optional">(optional)</span></label
        >
        <mat-form-field appearance="fill" class="full-width">
          <input
            matInput
            formControlName="inumber"
            id="inumber"
            placeholder="Invoice number"
          />
        </mat-form-field>

        <div class="file-upload-section">
          <label>Attach Invoice <span class="optional">(optional)</span></label>
          <input
            type="file"
            multiple
            accept=".pdf,.jpeg,.jpg,.png"
            (change)="onFileChange($event)"
            #fileInput
          />
          <button type="button" mat-stroked-button (click)="fileInput.click()">
            Choose Files
          </button>
          @if (submitForm.get("invoice")?.hasError("format")) {
            <mat-error>Only PDF, JPEG, and PNG files are allowed.</mat-error>
          }
          @if (submitForm.get("invoice")?.hasError("size")) {
            <mat-error>File size should be less than 5MB.</mat-error>
          }
          @if (uploadedFiles.length > 0) {
            <div class="uploaded-files">
              @for (file of uploadedFiles; track file; let i = $index) {
                <div class="file-item">
                  <span>{{ file.name }}</span>
                  <button
                    type="button"
                    mat-icon-button
                    (click)="removeFile(i)"
                    aria-label="Remove file"
                  >
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              }
            </div>
          }
        </div>

        <label for="memo">Memo <span class="optional">(optional)</span></label>
        <mat-form-field appearance="fill" class="full-width">
          <textarea
            matInput
            formControlName="memo"
            placeholder="What is this for?"
            rows="3"
            id="memo"
          ></textarea>
        </mat-form-field>
      </section>
    </form>
  </div>

  <div class="drw-footer">
    @if (anyOver) {
      <div class="footer-alert danger">{{ overMsg }}</div>
    }
    @if (needSplit) {
      <div class="footer-alert warn">
        A selected plan is missing its fee split %. Set the default split or a
        per-plan override.
      </div>
    }
    <button
      class="dialog-submit"
      mat-flat-button
      color="accent"
      [disabled]="!canSubmit || isSubmitting"
      (click)="openReview()"
    >
      Review &amp; send
    </button>
  </div>

  <!-- Review / success / error overlay -->
  @if (overlayStep !== "form") {
    <div class="overlay">
      <div class="overlay-header">
        <span class="drw-title">Review &amp; send</span>
        <button
          mat-icon-button
          (click)="overlayStep === 'sent' ? done() : closeReview()"
          aria-label="Close overlay"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>

      @if (overlayStep === "review") {
        <div class="overlay-body">
          <p class="overlay-lead">
            You're offering <strong>{{ summaryCountStr }}</strong> to
            <strong>{{ data.customer.borrowerName }}</strong
            >. They'll compare the options, pick one, and authorize its terms.
          </p>
          <div class="summary-box">
            <div class="info-row">
              <span class="label">Customer</span>
              <span class="value">{{ data.customer.borrowerName }}</span>
            </div>
            <div class="info-row">
              <span class="label">Your desired net</span>
              <span class="value strong">{{ summaryNetStr }}</span>
            </div>
            <div class="info-row">
              <span class="label">Request total range</span>
              <span class="value">{{ summaryRangeStr }}</span>
            </div>
          </div>
          <div class="summary-plans">
            @for (sp of summaryPlans; track sp.label) {
              <div class="summary-plan-card">
                <div class="summary-plan-top">
                  <div class="plan-name-row">
                    <span class="plan-name">{{ sp.label }}</span>
                    @if (sp.custom) {
                      <span class="custom-pill">Custom</span>
                    }
                  </div>
                  <span class="total">{{ sp.totalStr }}</span>
                </div>
                <div class="summary-plan-meta">
                  <span>{{ sp.splitStr }}</span>
                  <span>You net {{ sp.settleStr }}</span>
                </div>
              </div>
            }
          </div>
          <div class="info-callout">
            Where a plan includes a gross-up, the buyer sees a brief
            processing-fee disclosure in that plan's terms.
          </div>
        </div>
        <div class="overlay-footer">
          <button
            mat-button
            type="button"
            (click)="closeReview()"
            [disabled]="isSubmitting"
          >
            Back
          </button>
          <button
            mat-flat-button
            color="accent"
            class="flex-1"
            (click)="sendOffer()"
            [disabled]="isSubmitting"
          >
            @if (isSubmitting) {
              <mat-spinner [diameter]="20"></mat-spinner>
            } @else {
              Send offer
            }
          </button>
        </div>
      }

      @if (overlayStep === "error") {
        <div class="overlay-body centered">
          <div class="status-icon danger"><mat-icon>warning</mat-icon></div>
          <div class="status-title">Offer couldn't be sent</div>
          <p class="status-body">
            Something went wrong on our end and your offer to
            <strong>{{ data.customer.borrowerName }}</strong> wasn't delivered.
            No credit was reserved and they weren't notified. Your request
            details are saved — try again, or come back later.
          </p>
          <div class="error-code">Error · {{ errorTime }}</div>
        </div>
        <div class="overlay-footer">
          <button mat-button type="button" (click)="backFromError()">
            Back to review
          </button>
          <button
            mat-flat-button
            color="accent"
            class="flex-1"
            (click)="sendOffer()"
            [disabled]="isSubmitting"
          >
            Try again
          </button>
        </div>
      }

      @if (overlayStep === "sent") {
        <div class="overlay-body centered">
          <div class="status-icon success"><mat-icon>check</mat-icon></div>
          <div class="status-title">Offer sent</div>
          <p class="status-body">
            {{ data.customer.borrowerName }} will be notified to compare
            <strong>{{ summaryCountStr }}</strong> ({{ summaryRangeStr }}),
            choose one, and authorize its terms. You'll see it under Pending in
            Transactions until they respond.
          </p>
        </div>
        <div class="overlay-footer">
          <button
            mat-flat-button
            color="accent"
            class="flex-1"
            (click)="done()"
          >
            Done
          </button>
        </div>
      }
    </div>
  }
</div>
